import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Color from 'color';
import { Motion } from 'react-motion';
import { toggleTag } from '../../actions/search';
import { EMPTY_ARRAY, EMPTY_OBJECT, RESPONSIVE_SIZE } from '../../constants';
import { springify } from '../../lib/Motion';
import { groupTags } from '../../lib/tags';
import allTags from '../../assets/tags.json';
import Button from '../Button';
import {
  EmotionHappy,
  EmotionSad,
  EmotionExciting,
  EmotionChill,
  EmotionDark,
  Inside,
  Outside,
} from '../Icons';
import { Hover } from '../Hoverable';
import { H5, L1, L3, L4 } from '../Typography';
import Style from '../../components/Style';

const ICON_MAP = {
  [1800]: EmotionHappy,
  [1900]: EmotionSad,
  [2000]: EmotionExciting,
  [2100]: EmotionChill,
  [2200]: EmotionDark,
  [2600]: Inside,
  [2700]: Outside,
};

class TagToggles extends React.PureComponent {
  static propTypes = {
    responsive: PropTypes.number,
    activeTagIds: PropTypes.arrayOf(PropTypes.number),
    toggleTag: PropTypes.func,
    ref: PropTypes.func,
  };
  static defaultProps = {
    activeTagIds: EMPTY_ARRAY,
    toggleTag: () => {},
    ref: () => {},
  };

  constructor(props) {
    super(props);

    this.state = {
      allTags: groupTags(allTags),
    };
  }

  render() {
    let minButtonWidth = '5rem';
    if (this.props.responsive >= RESPONSIVE_SIZE.TABLET_PORTRAIT) {
      minButtonWidth = '10rem';
    }
    if (this.props.responsive >= RESPONSIVE_SIZE.TABLET_LANDSCAPE) {
      minButtonWidth = '13rem';
    }

    const { activeTagIds, toggleTag, ref, ...otherProps } = this.props;

    const typeStyle = {
      marginTop: '3rem',
      marginBottom: '2rem',
    };

    const groupStyle = {
      display: 'block',
      marginBottom: '0.5rem',
      fontWeight: 400,
      letterSpacing: '0.07rem',
      textTransform: 'uppercase',
      color: Style.Colors.shadow2,
      opacity: 0.5,
    };

    return (
      <div ref={ref}>
        <div>
          <H5 style={typeStyle}>Category</H5>
          <div style={{ margin: '0 -0.5rem 2rem', maxWidth: '75rem' }}>
            {this.state.allTags['Category']['undefined'].map(tag => (
              <Hover key={tag.id}>
                <RoundedButton
                  style={{ minWidth: minButtonWidth }}
                  checked={activeTagIds.includes(tag.id) ? true : undefined}
                  onClick={e => toggleTag(tag.id)}
                >
                  {tag.name}
                </RoundedButton>
              </Hover>
            ))}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            maxWidth: '80rem',
          }}
        >
          <div>
            <H5 style={{ ...typeStyle, marginBottom: '1rem' }}>Emotion</H5>
            <div style={{ margin: '0 2rem 0 -0.5rem' }}>
              {this.state.allTags['Emotion']['undefined'].map(tag => (
                <Hover key={tag.id}>
                  <IconButton
                    icon={ICON_MAP[tag.id]}
                    checked={activeTagIds.includes(tag.id) ? true : undefined}
                    onClick={e => toggleTag(tag.id)}
                  >
                    {tag.name}
                  </IconButton>
                </Hover>
              ))}
            </div>
          </div>
          <div>
            <H5 style={typeStyle}>Energy</H5>
            <ButtonGroup>
              {this.state.allTags['Energy'][' '].map(tag => (
                <Hover key={tag.id} style={{ minWidth: minButtonWidth }}>
                  <RoundedButton
                    checked={activeTagIds.includes(tag.id) ? true : undefined}
                    onClick={e => toggleTag(tag.id)}
                  >
                    {tag.name}
                  </RoundedButton>
                </Hover>
              ))}
            </ButtonGroup>
          </div>
        </div>
        <div>
          <H5 style={typeStyle}>Setting</H5>
          {['Location', 'Season', 'Time of day'].map(groupKey => {
            return (
              <div key={groupKey}>
                <L3 style={groupStyle}>{groupKey.toUpperCase()}</L3>
                <ButtonGroup>
                  {this.state.allTags['Setting'][groupKey].map(tag => {
                    return (
                      <Hover key={tag.id} style={{ minWidth: minButtonWidth }}>
                        <RoundedButton
                          checked={
                            activeTagIds.includes(tag.id) ? true : undefined
                          }
                          onClick={e => toggleTag(tag.id)}
                          icon={ICON_MAP[tag.id]}
                        >
                          {tag.name}
                        </RoundedButton>
                      </Hover>
                    );
                  })}
                </ButtonGroup>
              </div>
            );
          })}
        </div>
        <div>
          <H5 style={typeStyle}>Camera</H5>
          {['Framing', 'Movement', 'Speed'].map(groupKey => {
            return (
              <div key={groupKey}>
                <L3 style={groupStyle}>{groupKey.toUpperCase()}</L3>
                <ButtonGroup>
                  {this.state.allTags['Camera'][groupKey].map(tag => {
                    return (
                      <Hover key={tag.id} style={{ minWidth: minButtonWidth }}>
                        <RoundedButton
                          checked={
                            activeTagIds.includes(tag.id) ? true : undefined
                          }
                          onClick={e => toggleTag(tag.id)}
                        >
                          {tag.name}
                        </RoundedButton>
                      </Hover>
                    );
                  })}
                </ButtonGroup>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    responsive: state.runtime.responsive,
    activeTagIds: state.search.tags,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    toggleTag: tagId => dispatch(toggleTag(tagId)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(TagToggles);

class ButtonGroup extends React.PureComponent {
  static propTypes = {
    style: PropTypes.object,
  };
  static defaultProps = {
    style: EMPTY_OBJECT,
  };

  render() {
    let children = this.props.children;
    let childCount = React.Children.count(children);

    if (childCount > 1) {
      const firstStyle = {
        borderBottomRightRadius: 0,
        borderTopRightRadius: 0,
        marginRight: 0,
      };

      const middleStyle = {
        borderRadius: 0,
        marginLeft: '-0.15rem',
        marginRight: 0,
      };

      const lastStyle = {
        borderBottomLeftRadius: 0,
        borderTopLeftRadius: 0,
        marginLeft: '-0.15rem',
      };

      children = React.Children.map(children, (child, index) => {
        if (index === 0) {
          return React.cloneElement(child, {
            style: { ...child.props.style, ...firstStyle },
          });
        } else if (index + 1 === childCount) {
          return React.cloneElement(child, {
            style: { ...child.props.style, ...lastStyle },
          });
        } else {
          return React.cloneElement(child, {
            style: { ...child.props.style, ...middleStyle },
          });
        }
      });
    }

    return (
      <div style={{ margin: '0 -0.5rem 2rem', ...this.props.style }}>
        {children}
      </div>
    );
  }
}

class RoundedButton extends React.PureComponent {
  static propTypes = {
    hover: PropTypes.bool,
    checked: PropTypes.bool,
    icon: PropTypes.any,
    style: PropTypes.object,
  };
  static defaultProps = {
    hover: false,
    checked: false,
    icon: false,
    style: EMPTY_OBJECT,
  };

  render() {
    const { hover, checked, icon, style, children, ...otherProps } = this.props;

    let borderColor = Style.Colors.shadow3;
    let fillColor = Style.Colors.white;
    let labelColor = Style.Colors.shadow3;
    if (hover) {
      borderColor = Style.Colors.black;
      labelColor = Style.Colors.black;
    }
    if (checked) {
      borderColor = Style.Colors.black;
      fillColor = Style.Colors.black;
      labelColor = Style.Colors.white;
    }

    const buttonStyle = {
      position: hover ? 'relative' : 'static',
      zIndex: 2,
      display: 'inline-flex',
      justifyContent: 'space-around',
      alignItems: 'center',
      minWidth: '13rem',
      minHeight: '4rem',
      margin: '0.5rem',
      borderRadius: '0.7rem',
      border: `${borderColor} solid 0.15rem`,
      backgroundColor: fillColor,
      ...style,
    };
    const labelStyle = {
      padding: 0,
      margin: 0,
      letterSpacing: 0,
      color: labelColor,
    };

    let iconElement = null;
    if (icon) {
      const Icon = icon;
      iconElement = <Icon color={labelColor} />;
    }

    return (
      <button {...otherProps} style={buttonStyle}>
        {iconElement}
        <L1 style={labelStyle}>{children}</L1>
      </button>
    );
  }
}

class IconButton extends React.PureComponent {
  static propTypes = {
    icon: PropTypes.any.isRequired,
    hover: PropTypes.bool,
    checked: PropTypes.bool,
    style: PropTypes.object,
  };
  static defaultProps = {
    hover: false,
    checked: false,
    style: EMPTY_OBJECT,
  };

  render() {
    const { hover, checked, icon, style, children, ...otherProps } = this.props;

    let fillColor = Style.Colors.shadow4;
    if (hover) {
      fillColor = Style.Colors.shadow3;
    }
    if (checked) {
      fillColor = Style.Colors.black;
    }

    const buttonStyle = {
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      border: 0,
      padding: '0 0.35rem',
      ...style,
    };
    const labelStyle = {
      fontWeight: 400,
      letterSpacing: '0.07rem',
      textTransform: 'uppercase',
      color: fillColor,
    };
    const iconStyle = {
      maxWidth: '6.5rem',
      margin: '-0.5rem',
    };

    const Icon = icon;
    const iconElement = <Icon color={fillColor} style={iconStyle} />;

    return (
      <button {...otherProps} style={buttonStyle}>
        {iconElement}
        <L4 style={labelStyle}>{children}</L4>
      </button>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/TagToggles/TagToggles.js